import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { AccessComponentEnum } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import {
  AiIndexingSection,
  AiUsageSection,
  PlanEnabledChip,
  PlanSectionTitle,
  PlanUsersSection,
} from '@lib/components/CompanyPlanView';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import AiUsageByUserModal from './components/AiUsageByUserModal';
import IndexingDocumentsModal from './components/IndexingDocumentsModal';
import ParkedDocumentsModal from './components/ParkedDocumentsModal';
import TopUsersSection from './components/TopUsersSection';
import {
  FAILED_FILTERS,
  SKIPPED_FILTERS,
} from './utils/getIndexingDocumentFilters';
import useActions from './utils/useActions';

const modals = [
  Modals.CompanyPlanFailedModal,
  Modals.CompanyPlanParkedModal,
  Modals.CompanyPlanSkippedModal,
  Modals.CompanyPlanUsageModal,
];

export default function CompanyPlan() {
  const { t } = useTranslation();

  const { closeModal, isModalOpened, openModal } = useModalManagement(modals);

  const {
    activeUsersCount,
    breadCrumbsData,
    budgetData,
    hasTopUsers,
    indexingData,
    isAccessEnabled,
    isAiAssistanceEnabled,
    isAllFeaturesEnabled,
    isFullAdmin,
    onRetryParkedFromModal,
    onUpdatePlan,
    retryParked,
    retryParkedLoading,
    topUsersCollection,
    topUsersLoading,
    topUsersOrderingState,
    topUsersPaginationState,
    usersLimit,
  } = useActions({ closeModal });

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <PageBody withoutHeader>
        <DashboardContentMaxWidth>
          <WhiteBox>
            <DashboardTitleHeader
              title={t('company-plan')}
              bottomSpace="none"
              actions={[
                <Button
                  key="updatePlan"
                  buttonText={t(
                    isAllFeaturesEnabled ? 'contact-us' : 'update-plan',
                  )}
                  buttonType={ButtonTypes.primaryOutlined}
                  data-test-id="company-plan__update-plan-button"
                  onClick={onUpdatePlan}
                />,
              ]}
            />
            <div className={entityViewStyles.infoList}>
              <PlanSectionTitle title={t('users')} />
              <PlanUsersSection
                activeUsersCount={activeUsersCount}
                usersLimit={usersLimit}
              />

              <hr className={entityViewStyles.hrLine} />

              <PlanSectionTitle
                title={t('intervention-management')}
                chip={
                  <PlanEnabledChip
                    enabled={isAccessEnabled(
                      AccessComponentEnum.InterventionsManagement,
                    )}
                    disabledTooltip={t('not-included-in-your-current-plan')}
                  />
                }
              />

              <hr className={entityViewStyles.hrLine} />

              <PlanSectionTitle
                title={t('documentation-management')}
                chip={
                  <PlanEnabledChip
                    enabled={isAccessEnabled(
                      AccessComponentEnum.DocumentationManagement,
                    )}
                    disabledTooltip={t('not-included-in-your-current-plan')}
                  />
                }
              />

              <hr className={entityViewStyles.hrLine} />

              <PlanSectionTitle
                title={t('ai-assistance')}
                chip={
                  <PlanEnabledChip
                    enabled={isAiAssistanceEnabled}
                    disabledTooltip={t('not-included-in-your-current-plan')}
                  />
                }
              />
              {isAiAssistanceEnabled && (
                <>
                  <AiUsageSection
                    usedCredits={_get(budgetData, 'data.usedCredits')}
                    totalCredits={_get(budgetData, 'data.totalCredits')}
                    totalUsagePercentage={_get(budgetData, 'data.percentage')}
                    userUsagePercentage={_get(
                      budgetData,
                      'data.userPercentage',
                    )}
                    systemUsagePercentage={_get(
                      budgetData,
                      'data.systemPercentage',
                    )}
                  />
                  <TopUsersSection
                    hasTopUsers={hasTopUsers}
                    onViewAll={() => openModal(Modals.CompanyPlanUsageModal)}
                    topUsersCollection={topUsersCollection}
                    topUsersLoading={topUsersLoading}
                    topUsersOrderingState={topUsersOrderingState}
                    topUsersPaginationState={topUsersPaginationState}
                  />

                  <hr className={entityViewStyles.hrLine} />

                  <AiIndexingSection
                    coverage={_get(indexingData, 'data')}
                    // Only ever the signed-in company's own card, and logout
                    // clears the store, so one scope is enough here.
                    storageId="own-company"
                    onRetryParked={retryParked}
                    retryParkedLoading={retryParkedLoading}
                    // Gated on the role, not just hidden: the query behind the
                    // modal filters on `aiIndexingRetryLane`, and a rejected
                    // filter nulls the WHOLE response rather than dropping a
                    // field — so for anyone but a full admin it would open an
                    // empty list, not a partial one.
                    onParkedClick={
                      isFullAdmin
                        ? () => openModal(Modals.CompanyPlanParkedModal)
                        : undefined
                    }
                    // Gated the same way: `aiIndexingStatus` filters are also
                    // full-admin-only, and a rejected filter nulls the whole
                    // response.
                    onSkippedClick={
                      isFullAdmin
                        ? () => openModal(Modals.CompanyPlanSkippedModal)
                        : undefined
                    }
                    onFailedClick={
                      isFullAdmin
                        ? () => openModal(Modals.CompanyPlanFailedModal)
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          </WhiteBox>
        </DashboardContentMaxWidth>
      </PageBody>
      <AiUsageByUserModal
        isOpen={isModalOpened(Modals.CompanyPlanUsageModal)}
        onClose={() => closeModal(Modals.CompanyPlanUsageModal)}
      />
      {/* A successful retry empties the parked set, so the list the modal is
          showing stops existing — close it and let the recomputed card behind
          it (parked: 0, SCHEDULED) be the answer. */}
      <ParkedDocumentsModal
        isOpen={isModalOpened(Modals.CompanyPlanParkedModal)}
        onClose={() => closeModal(Modals.CompanyPlanParkedModal)}
        onRetryParked={onRetryParkedFromModal}
        retryParkedLoading={retryParkedLoading}
      />
      <IndexingDocumentsModal
        isOpen={isModalOpened(Modals.CompanyPlanSkippedModal)}
        onClose={() => closeModal(Modals.CompanyPlanSkippedModal)}
        title={t('ai-not-indexable-documents')}
        description={t('ai-not-indexable-documents-description')}
        filters={SKIPPED_FILTERS}
        includeReasonColumn={false}
      />
      <IndexingDocumentsModal
        isOpen={isModalOpened(Modals.CompanyPlanFailedModal)}
        onClose={() => closeModal(Modals.CompanyPlanFailedModal)}
        title={t('ai-failed-documents')}
        description={t('ai-failed-documents-description')}
        filters={FAILED_FILTERS}
      />
    </>
  );
}
